import React, { useRef, useContext, useEffect } from "react";
import ReactPlayer from "react-player";
import { Box, IconButton, Typography, Slider, Paper, useTheme } from "@mui/material";
import { PlayArrow, Pause, VolumeUp, VolumeOff } from "@mui/icons-material";
import { PlayerContext } from "../../context/PlayerContext";
import HeadbandRadio from "../../views/Radio/components/HeadbandRadio";
import { getResourceInTimeforSong } from "../../utils/functions";
import PlayerMenu from "../../views/Radio/components/PlayerMenu";
import PlatformSharingComponent from "../../views/Magazine/NewsDeployment/MusicChannels/PlatformSharingComponent";

const MusicPlayer = ({ autoplay = true, programName = "Hits Latinos" }) => {
    const {
        isPlaying,
        open,
        setOpen,
        handleOpen,
        setIsPlaying,
        currentSongIndex,
        setCurrentSongIndex,
        playlist,
        currentPlaylist,
        setSecondToPlay,
        totalDuration,
        secondToPlay,
        setIsMuted,
        isMuted,
        setVolumeLevel,
        volumeLevel,
        setPreviousSongIndex,
        setNextSongIndex,
        setLoadingSong,
    } = useContext(PlayerContext);
    const audioRef = useRef(null);
    const theme = useTheme();

    useEffect(() => {
        // Auto-start if autoplay is true
        if (autoplay) {
            setIsPlaying(true);
        }
    }, [autoplay]);

    const handleSkipNext = () => {
        setSecondToPlay(0);
        if (currentPlaylist.length === currentSongIndex) {
            setCurrentSongIndex(0);
        } else {
            setCurrentSongIndex((prevIndex) => (prevIndex + 1) % currentPlaylist.length);
        }
    };
    const handleOnReady = (e) => {
        if (autoplay) {
            const { videoIndex, playtime, previousIndex, nextIndex } = getResourceInTimeforSong(
                currentPlaylist,
                totalDuration,
            );
            setSecondToPlay(playtime);
            setCurrentSongIndex(videoIndex);
            setPreviousSongIndex(previousIndex);
            setNextSongIndex(nextIndex);
        }
        audioRef.current.seekTo(secondToPlay);
    };
    const handleMuteToggle = () => {
        setIsMuted(!isMuted);
    };

    const handleVolumeChange = (_, newValue) => {
        setVolumeLevel(newValue);
    };
    const getCurrentSong = () => {
        if (playlist && playlist.length > 0) {
            return playlist[currentSongIndex];
        }
        return null;
    };
    const currentSong = getCurrentSong();

    const hasHeadlines =
        currentSong && currentSong.radio_headlines && currentSong.radio_headlines.length > 0;
    return (
        <Paper
            elevation={4}
            sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: (theme) => `${theme.palette.background.paper}98`,
                color: "text.primary",
                backdropFilter: "blur(20px)",
                translate: open ? "0 0" : "0 100%",
                transition: "translate 0.3s ease-in-out",
            }}
        >
            <PlayerMenu
                isMuted={isMuted}
                setIsMuted={setIsMuted}
                volumeLevel={volumeLevel}
                setVolumeLevel={setVolumeLevel}
                setOpen={setOpen}
            />
            <Box sx={{ width: "100%", position: "relative", zIndex: 2 }}>
                {hasHeadlines ? (
                    <HeadbandRadio
                        currentHeadlinesList={currentSong.radio_headlines}
                        currentSong={currentSongIndex}
                        fontSizeW={18}
                        fontSizeM={15}
                    />
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            color: "white",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: "0.5rem",
                            padding: "0.8rem 1rem",
                        }}
                    >
                        <Typography sx={{ fontSize: "18px", textAlign: "center" }}>
                            Sintoniza Alta-Voz Radio para las mejores noticias y música
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ px: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Box
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                px: 0.3,
                                py: 0.4,
                                borderRadius: "12px",
                                fontSize: "0.55rem",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "50px",
                            }}
                        >
                            En directo
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {programName}
                        </Typography>
                    </Box>
                    <Box sx={{ position: "relative" }}>
                        <Box
                            component="img"
                            src={currentSong?.url_image || "/placeholder-album.jpg"}
                            alt={currentSong?.titulo || "Album cover"}
                            sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover" }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 0, sm: 1 },
                            flexGrow: 1,
                            justifyContent: "center",
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {currentSong?.artistName}
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                display: { xs: "none", sm: "block" },
                            }}
                        >
                            -
                        </Typography>
                        <Typography variant="subtitle1">{currentSong?.songName}</Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={handleOpen}
                    sx={{
                        width: { xs: 38, sm: 48 },
                        height: { xs: 38, sm: 48 },
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                        color: theme.palette.primary.main,
                    }}
                >
                    {open ? <Pause /> : <PlayArrow />}
                </IconButton>
                <Box
                    sx={{
                        display: { xs: "none", sm: "flex" },
                        alignItems: "center",
                        gap: { xs: 1, sm: 3 },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton onClick={handleMuteToggle} size="small">
                            {isMuted ? (
                                <VolumeOff sx={{ color: theme.palette.primary.main }} />
                            ) : (
                                <VolumeUp sx={{ color: theme.palette.primary.main }} />
                            )}
                        </IconButton>
                        <Slider
                            size="small"
                            value={volumeLevel}
                            onChange={handleVolumeChange}
                            sx={{ width: { xs: 50, sm: 100 } }}
                        />
                    </Box>

                    <IconButton>
                        <PlatformSharingComponent
                            shareText="Alta-Voz Radio:"
                            currentUrl={`${process.env.REACT_APP_URL_FRONT}altavozradio`}
                        />
                    </IconButton>
                </Box>
            </Box>
            {playlist.length > 0 && (
                <ReactPlayer
                    ref={audioRef}
                    url={currentSong?.url || ""}
                    playing={isPlaying}
                    volume={volumeLevel / 100}
                    muted={isMuted}
                    width="0"
                    height="0"
                    onReady={() => {
                        handleOnReady();
                        setLoadingSong(false);
                    }}
                    onEnded={handleSkipNext}
                    config={{
                        file: {
                            forceAudio: true,
                        },
                        youtube: {
                            playerVars: {
                                showinfo: 0,
                                modestbranding: 1,
                                rel: 0,
                                controls: 0,
                                autoplay: 1,
                                disablekb: 1,
                            },
                        },
                    }}
                />
            )}
        </Paper>
    );
};

export default MusicPlayer;
